import { create, persist } from '@/libs';
import type { CustomerGuestDocument } from '@/types/models';
import type { RegistrationFormData } from '@/types/models/CustomerRegistrationForm';

const COOKIE_NAME = 'customer-store';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 30;

const writeCustomerCookie = (data: CustomerDataWithDocument | null) => {
  if (typeof document === 'undefined') {
    return;
  }

  if (!data) {
    document.cookie = `${COOKIE_NAME}=; path=/; max-age=0`;
    return;
  }

  const cookieValue = encodeURIComponent(
    JSON.stringify({
      state: {
        customerData: data,
      },
    }),
  );

  document.cookie = `${COOKIE_NAME}=${cookieValue}; path=/; max-age=${COOKIE_MAX_AGE}; sameSite=lax`;
};

export type CustomerDataWithDocument = CustomerGuestDocument & {
  document?: string;
  email?: string;
  phone?: string;
  hasAccount: boolean;
  hasMail: boolean;
  hasAddress: boolean;
  pendingRegistrationData?: RegistrationFormData;
};

type CustomerStore = {
  customerData: CustomerDataWithDocument | null;
  setCustomerData: (data: CustomerDataWithDocument) => void;
  clearCustomerData: () => void;
};

export const useCustomerStore = create<CustomerStore>()(
  persist(
    (set, get) => ({
      customerData: null,
      setCustomerData: (data) => {
        const currentData = get().customerData;

        if (JSON.stringify(currentData) !== JSON.stringify(data)) {
          set({ customerData: data });
          writeCustomerCookie(data);
        }
      },
      clearCustomerData: () => {
        set({ customerData: null });
        writeCustomerCookie(null);
      },
    }),
    {
      name: 'customer-store',
    },
  ),
);
